// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: in the iOS/Android shell, calls run natively (CallKit / Telecom +
// LiveKit's native SDKs + a native gateway session) so they survive a locked
// phone. This store mirrors the native call and is the web UI's remote control.
// See konvo_mobile/NATIVE_CONTRACT.md ("Why calls are native end to end").

import Channels from '@app/features/channel/state/Channels';
import {getDMDisplayName} from '@app/features/channel/utils/ChannelUtils';
import Guilds from '@app/features/guild/state/Guilds';
import {
	type KonvoCallsPlugin,
	type NativeCallState,
	NativePlugins,
	type NativeScreenShareInfo,
	type NativeScreenSharePreview,
} from '@app/features/platform/native_mobile/NativeMobileBridge';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Users from '@app/features/user/state/Users';
import {getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('NativeCallSession');

const IDLE_STATE: NativeCallState = {
	phase: 'idle',
	channelId: null,
	guildId: null,
	connectionId: null,
	title: null,
	muted: false,
	deafened: false,
	speaker: false,
	audioRoute: 'unknown',
	startedAt: null,
	endedReason: null,
	error: null,
};

const ACTIVE_PHASES = new Set<NativeCallState['phase']>(['outgoing', 'connecting', 'connected', 'reconnecting']);

export interface NativeConnectHooks {
	shouldStartVoiceConnection?: () => boolean;
	beforeVoiceStateConnect?: () => Promise<void>;
	onVoiceStateConnect?: () => void;
	onVoiceConnectCancelled?: (error?: unknown) => void;
}

export function describeCallTarget(
	guildId: string | null,
	channelId: string,
): {title: string; avatarUrl: string | null} {
	const channel = Channels.getChannel(channelId);
	if (!channel) return {title: 'Konvo call', avatarUrl: null};
	if (channel.isPrivate()) {
		const title = getDMDisplayName(channel);
		const otherUser = channel.recipientIds.length === 1 ? Users.getUser(channel.recipientIds[0]) : null;
		return {title, avatarUrl: otherUser ? getUserAvatarURL(otherUser, false, 256) : null};
	}
	const guild = guildId ? Guilds.getGuild(guildId) : null;
	const title = guild ? `${channel.name ?? ''} · ${guild.name}` : (channel.name ?? 'Voice channel');
	return {title, avatarUrl: null};
}

class NativeCallSession {
	state: NativeCallState = IDLE_STATE;
	speakingUserIds: ReadonlyArray<string> = [];
	sheetOpen = false;
	screenShareError: 'unsupported' | 'unavailable' | null = null;
	openingScreenShare: string | null = null;
	private initialized = false;

	constructor() {
		makeAutoObservable<this, 'initialized'>(this, {initialized: false}, {autoBind: true});
	}

	private get plugin(): KonvoCallsPlugin | null {
		return NativePlugins.calls();
	}

	/** True inside the shell: all calls go through the native engine. */
	get supported(): boolean {
		return this.plugin !== null;
	}

	get active(): boolean {
		return ACTIVE_PHASES.has(this.state.phase);
	}

	get activeChannelId(): string | null {
		return this.active ? this.state.channelId : null;
	}

	isActiveIn(channelId: string): boolean {
		return this.active && this.state.channelId === channelId;
	}

	isIncoming(channelId: string): boolean {
		return this.state.phase === 'incoming' && this.state.channelId === channelId;
	}

	init(): () => void {
		const plugin = this.plugin;
		if (!plugin || this.initialized) return () => {};
		this.initialized = true;
		// A reload drops the page that placed a live preview card, but the native view stays over
		// the web view; clear it before any card is drawn again. Older shells lack the method.
		if (typeof plugin.hideScreenSharePreview === 'function') {
			void plugin.hideScreenSharePreview().catch(() => undefined);
		}
		const stateHandle = plugin.addListener('state', (next) => this.applyState(next));
		const speakingHandle = plugin.addListener('speaking', ({userIds}) => {
			runInAction(() => {
				this.speakingUserIds = userIds;
			});
		});
		void plugin
			.getState()
			.then((next) => this.applyState(next))
			.catch((error) => logger.warn('Failed to read native call state', error));
		return () => {
			this.initialized = false;
			void stateHandle.then((handle) => handle.remove());
			void speakingHandle.then((handle) => handle.remove());
		};
	}

	private applyState(next: NativeCallState): void {
		const wasActive = this.active;
		this.state = {...IDLE_STATE, ...next};
		if (!this.active) {
			this.screenShareError = null;
			this.openingScreenShare = null;
			this.speakingUserIds = [];
			if (wasActive) this.sheetOpen = false;
		}
	}

	/** Called by MediaEngineFacade instead of joining a LiveKit room in the WebView. */
	async connect(guildId: string | null, channelId: string, hooks: NativeConnectHooks = {}): Promise<boolean> {
		const plugin = this.plugin;
		if (!plugin) return false;
		if (hooks.shouldStartVoiceConnection && !hooks.shouldStartVoiceConnection()) {
			hooks.onVoiceConnectCancelled?.();
			return false;
		}
		if (this.isActiveIn(channelId)) {
			this.openSheet();
			hooks.onVoiceStateConnect?.();
			return true;
		}
		try {
			if (this.isIncoming(channelId)) {
				await plugin.answerCall({channelId});
			} else {
				await hooks.beforeVoiceStateConnect?.();
				if (hooks.shouldStartVoiceConnection && !hooks.shouldStartVoiceConnection()) {
					hooks.onVoiceConnectCancelled?.();
					return false;
				}
				const target = describeCallTarget(guildId, channelId);
				await plugin.startCall({
					channelId,
					guildId,
					title: target.title,
					avatarUrl: target.avatarUrl,
					selfMute: LocalVoiceState.getSelfMute(),
				});
			}
			hooks.onVoiceStateConnect?.();
			this.openSheet();
			return true;
		} catch (error) {
			logger.error('Native call failed to start', error);
			hooks.onVoiceConnectCancelled?.(error);
			return false;
		}
	}

	/** The shell can draw a live preview card in the call sheet (older shells only have the viewer). */
	get screenSharePreviewSupported(): boolean {
		return this.state.screenShareViewingSupported === true && this.state.screenSharePreviewSupported === true;
	}

	/** Places (or moves) the native live preview over the call sheet's card. False: not shown (yet). */
	async showScreenSharePreview(preview: NativeScreenSharePreview): Promise<boolean> {
		const plugin = this.plugin;
		if (!plugin || !this.screenSharePreviewSupported || !this.isActiveIn(preview.channelId)) return false;
		try {
			await plugin.showScreenSharePreview(preview);
			return true;
		} catch (error) {
			// The share ended, or native hasn't caught up with the call yet; the card retries.
			logger.debug('Native screen preview unavailable', error);
			return false;
		}
	}

	async hideScreenSharePreview(): Promise<void> {
		const plugin = this.plugin;
		if (!plugin || !this.screenSharePreviewSupported) return;
		try {
			await plugin.hideScreenSharePreview();
		} catch (error) {
			logger.debug('Failed to hide native screen preview', error);
		}
	}

	async watchScreenShare(channelId: string, connectionId: string, info?: NativeScreenShareInfo): Promise<void> {
		if (this.openingScreenShare) return;
		this.screenShareError = null;
		if (!this.state.screenShareViewingSupported) {
			this.screenShareError = 'unsupported';
			return;
		}
		const plugin = this.plugin;
		if (
			!plugin ||
			!this.isActiveIn(channelId) ||
			this.state.phase !== 'connected' ||
			!this.state.screenShares?.some((share) => share.connectionId === connectionId)
		) {
			this.screenShareError = 'unavailable';
			return;
		}
		this.openingScreenShare = connectionId;
		try {
			await plugin.watchScreenShare(info ? {channelId, connectionId, info} : {channelId, connectionId});
		} catch (error) {
			logger.warn('Failed to open native screen share', error);
			runInAction(() => {
				if (this.isActiveIn(channelId)) this.screenShareError = 'unavailable';
			});
		} finally {
			runInAction(() => {
				this.openingScreenShare = null;
			});
		}
	}

	async hangUp(): Promise<void> {
		await this.plugin?.endCall();
	}

	async setMuted(muted: boolean): Promise<void> {
		await this.plugin?.setMuted({muted});
	}

	async setDeafened(deafened: boolean): Promise<void> {
		await this.plugin?.setDeafened({deafened});
	}

	async setSpeaker(on: boolean): Promise<void> {
		await this.plugin?.setSpeaker({on});
	}

	openSheet(): void {
		this.sheetOpen = true;
	}

	closeSheet(): void {
		this.sheetOpen = false;
	}
}

export default new NativeCallSession();
